import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { EyeIcon, EyeOffIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import type { InputProps } from '#/shared/ui/input'

const MASKED_DOTS_CLASS = '[&_input]:text-xl [&_input]:tracking-wider'

type PasswordInputProps = Omit<InputProps, 'type' | 'endAdornment'>

function VisibilityToggle({
  visible,
  onToggle,
  label,
}: {
  visible: boolean
  onToggle: () => void
  label: string
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      aria-pressed={visible}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
      className="flex size-4.5 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-gray-900"
    >
      {visible ? (
        <EyeOffIcon className="size-4.5" />
      ) : (
        <EyeIcon className="size-4.5" />
      )}
    </button>
  )
}

export function PasswordInput({ className, ...props }: PasswordInputProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  const hasValue = typeof props.value === 'string' && props.value.length > 0
  const masked = !visible && hasValue

  return (
    <Input
      {...props}
      className={cn(className, masked && MASKED_DOTS_CLASS)}
      type={visible ? 'text' : 'password'}
      endAdornment={
        <VisibilityToggle
          visible={visible}
          onToggle={() => setVisible((prev) => !prev)}
          label={visible ? t('passwordInput.hide') : t('passwordInput.show')}
        />
      }
    />
  )
}

export type { PasswordInputProps }
